/** One atomic graphic selector. String shapes preserve the established authored shape contract. */
export type SimpleShapeId = 'circle' | 'square' | 'triangle' | 'diamond';
export type SymbolGraphic<Shape extends SimpleShapeId = SimpleShapeId> =
  | Shape
  | { readonly kind: 'asset'; readonly assetId: string }
  | { readonly kind: 'builtin'; readonly id: BuiltinSymbolId };
/** Versioned artwork identities; changing artwork requires a new catalogue version. */
export const BUILTIN_SYMBOL_IDS = [
  'placeio-v1:pin',
  'placeio-v1:flag',
  'placeio-v1:information',
  'placeio-v1:star',
  'placeio-v1:bus',
  'placeio-v1:rail',
  'placeio-v1:metro',
  'placeio-v1:tram',
  'placeio-v1:ferry',
  'placeio-v1:bicycle',
  'placeio-v1:parking',
  'placeio-v1:airport',
  'placeio-v1:school',
  'placeio-v1:hospital',
  'placeio-v1:park',
  'placeio-v1:construction',
  'placeio-v1:walking',
  'placeio-v1:library',
] as const;
export type BuiltinSymbolId = (typeof BUILTIN_SYMBOL_IDS)[number];
export function isBuiltinGraphic(
  value: unknown,
): value is Extract<SymbolGraphic, { kind: 'builtin' }> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 2 &&
    'kind' in value &&
    value.kind === 'builtin' &&
    'id' in value &&
    BUILTIN_SYMBOL_IDS.includes(value.id as BuiltinSymbolId)
  );
}
export function isImageGraphic(value: unknown): value is Exclude<SymbolGraphic, string> {
  return isAssetGraphic(value) || isBuiltinGraphic(value);
}
export type SymbolMimeType = 'image/png' | 'image/svg+xml';
export interface SymbolAsset {
  readonly id: string;
  readonly name: string;
  readonly mimeType: SymbolMimeType;
  readonly bytes: Uint8Array;
  readonly sha256: string;
  readonly width: number;
  readonly height: number;
}
export interface SymbolAssetDescriptor extends Omit<SymbolAsset, 'bytes'> {
  readonly resource: { readonly path: string; readonly encodingVersion: 1 };
}
/** Validated artwork identity. Renaming an asset does not invalidate prepared images. */
export function symbolLibraryIdentity(assets: readonly SymbolAsset[]): string {
  return JSON.stringify(
    assets.map(({ id, sha256, mimeType, width, height }) => [id, sha256, mimeType, width, height]),
  );
}
export const SYMBOL_LIMITS = {
  pngBytes: 5 * 1024 * 1024,
  svgBytes: 512 * 1024,
  dimension: 4096,
  pixels: 16_000_000,
  retainedDimension: 2048,
  assets: 64,
  totalBytes: 32 * 1024 * 1024,
  cacheBytes: 32 * 1024 * 1024,
  elements: 2000,
  depth: 32,
  commands: 20_000,
  numbers: 100_000,
  attributeLength: 128 * 1024,
} as const;
export class SymbolImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SymbolImportError';
  }
}
export function isAssetGraphic(
  value: unknown,
): value is { readonly kind: 'asset'; readonly assetId: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 2 &&
    'kind' in value &&
    value.kind === 'asset' &&
    'assetId' in value &&
    typeof value.assetId === 'string' &&
    /^sym_[A-Za-z0-9_-]{1,80}$/.test(value.assetId)
  );
}
export function symbolAssetPath(asset: Pick<SymbolAsset, 'id' | 'mimeType'>): string {
  return `assets/symbols/${asset.id}.${asset.mimeType === 'image/png' ? 'png' : 'svg'}`;
}
export function symbolName(value: string): string {
  // eslint-disable-next-line no-control-regex -- Names must not retain control characters.
  const clean = value.replace(/[\u0000-\u001f\u007f/\\]/g, '').trim();
  if (!clean || clean.length > 120)
    throw new SymbolImportError('Use a symbol name between 1 and 120 characters.');
  return clean;
}
export function validateSymbolDimensions(width: number, height: number): void {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    width / height < 0.1 ||
    width / height > 10
  ) {
    throw new SymbolImportError(
      'This symbol has invalid dimensions or an aspect ratio outside 1:10–10:1.',
    );
  }
}
export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
export async function symbolHash(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes).buffer);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export function isSymbolBytes(value: unknown): value is Uint8Array {
  return (
    ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === '[object Uint8Array]'
  );
}
